/**
 * The unscheduled queue — the left column of `scheduling-main.png`.
 *
 * It follows the board's filters, which is the point: "38 jobs · 214 h" has to
 * mean the work this view is about, or the number is decoration. Items drag
 * onto a crew's day.
 */
import { Tag } from './primitives.js'
import { formatDateOnly, formatNumber } from '../lib/format.js'
import { cn } from '../lib/cn.js'
import type { QueueItem } from '../lib/schedule-types.js'

export function UnscheduledQueue({
  items, totals, selectedId, onSelect, editable,
}: {
  items: QueueItem[]
  totals: { items: number; hours: number; overdue: number }
  selectedId?: string | null
  onSelect?: (item: QueueItem) => void
  editable?: boolean
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <span className="text-card font-semibold text-text">Unscheduled</span>
        <span className="tnum rounded-full bg-surface-alt px-2 py-0.5 text-sub text-text-muted">
          {formatNumber(totals.items)} {totals.items === 1 ? 'item' : 'items'} ·{' '}
          {formatNumber(totals.hours)} h
        </span>
        {totals.overdue > 0 && (
          <span className="tnum rounded-full bg-danger-bg px-2 py-0.5 text-sub font-semibold text-danger">
            Overdue · {totals.overdue}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {items.length === 0 && (
          <p className="m-0 px-3 py-8 text-center text-cell text-text-muted">
            Nothing waiting. Everything in this view has hours on a day.
          </p>
        )}

        {items.map((item) => (
          <button
            key={item.workItemId}
            type="button"
            draggable={editable}
            onDragStart={(event) => {
              event.dataTransfer.setData('application/json', JSON.stringify({
                kind: 'workItem',
                workItemId: item.workItemId,
                hours: item.remainingHours,
              }))
              event.dataTransfer.effectAllowed = 'copy'
            }}
            onClick={() => onSelect?.(item)}
            className={cn(
              'block w-full border-b border-line-soft px-3 py-2 text-left',
              selectedId === item.workItemId ? 'bg-accent-soft' : 'hover:bg-surface-alt',
              item.overdueDays > 0 && selectedId !== item.workItemId && 'bg-danger-bg',
            )}
          >
            <span className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-cell font-semibold text-text">
                {item.propertyName}
              </span>
              {item.overdueDays > 0 && (
                <Tag tone="bad">Overdue {item.overdueDays}d</Tag>
              )}
            </span>

            <span className="block truncate text-sub text-text-muted">
              {item.description}
              {item.zoneName && ` · ${item.zoneName}`}
              {' · '}
              {/* What is left to place, not the budget — dragging places this. */}
              {formatNumber(item.remainingHours)} h
              {item.scheduledHours > 0
                && ` of ${formatNumber(item.budgetedHours)} h`}
            </span>

            <span className="mt-1 flex flex-wrap items-center gap-1">
              {item.requires.map((need) => <Tag key={need}>{need}</Tag>)}
              {(item.earliestOn || item.dueOn) && (
                <span className="text-sub text-text-faint">
                  {item.earliestOn && item.dueOn
                    ? `Window ${formatDateOnly(item.earliestOn)} – ${formatDateOnly(item.dueOn)}`
                    : item.dueOn
                      ? `By ${formatDateOnly(item.dueOn)}`
                      : `From ${formatDateOnly(item.earliestOn)}`}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
